import type {
  CreateParams,
  DeleteManyParams,
  DeleteParams,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetOneParams,
  RaRecord,
  UpdateManyParams,
  UpdateParams,
} from 'react-admin';
import type {
  AdminDataProvider,
  AuditEntryDraft,
  AuditSink,
  Currency,
  Dataset,
  Market,
} from './types';
import { CURRENCY_BY_MARKET, marketsForScope } from './types';

/**
 * In-memory mock DataProvider implementing React Admin's standard interface
 * exactly, so surfaces can be built against it now and the whole thing swapped
 * for a real API later behind the same interface (one-file change — see
 * ./index.ts). Mutations mutate the in-memory store for the session only.
 *
 * Two behaviours worth knowing:
 *
 * 1. THE MARKET MAPPING (D-region-mapping) lives in ONE place here. The admin
 *    speaks the access vocabulary (uppercase `UK | US | GLOBAL`, a Cognito
 *    attribute); rows speak the data vocabulary (lowercase `market: uk | us | ie`).
 *    A `region` filter is translated to the set of markets it grants and applied
 *    to any resource whose rows carry a `market` field. Resources without one
 *    (merchants, categories, networks, tags, charity types, join tables) are
 *    market-agnostic and the region filter is ignored for them. There is no
 *    server-side enforcement here (there is no server).
 *
 * 2. DERIVED FIELDS are computed on read, never stored in the seed: a user's
 *    `balance` (confirmed cashback minus in-flight/settled cashouts), an
 *    affiliate network's `storeCount`, and merchant / merchant_region `clicks`.
 *    They are materialised before filter/sort so surfaces can sort by them.
 *
 * `getList` also honours pagination, multi-field sort, equality filters,
 * array-field membership (e.g. filter merchant_regions by a `tagKeys` value),
 * and a `q` case-insensitive substring search.
 *
 * The object is cast `as unknown as DataProvider`: the interface methods are
 * generic over the record type, which a concrete in-memory store can't satisfy
 * structurally. Internally everything is typed against `Row`.
 */

type Row = RaRecord<number> & Record<string, unknown>;
type Db = Record<string, Row[]>;

const clone = <T>(value: T): T => structuredClone(value);
const round2 = (n: number): number => Math.round(n * 100) / 100;

// The one region -> markets mapping (D-region-mapping) now lives in ./types.ts
// (`marketsForScope`), so the Earnings surface can share the exact same mapping
// rather than re-deriving it. Imported above.

// --- derived fields ---------------------------------------------------------
/**
 * The TRUE SIGNED balance: confirmed cashback, minus in-flight/settled cashouts,
 * plus the signed BALANCE LEDGER (D-balance-adjustment). The ledger is ONE signed
 * term holding both chargeback reversals (always negative, D-chargebacks) and manual
 * adjustments (a credit positive, a debit negative), so value only ever moves through
 * a recorded entry, never a direct write. Can go negative (a clawback or a debit
 * exceeding the remaining balance is a real loss the admin must see).
 */
const deriveBalanceSigned = (db: Db, userId: number): number => {
  const confirmed = (db.transactions ?? [])
    .filter((t) => t.userId === userId && t.status === 'confirmed')
    .reduce((sum, t) => sum + Number(t.cashbackAmount), 0);
  const inFlight = (db.cashoutRequests ?? [])
    .filter(
      (c) =>
        c.userId === userId &&
        (c.status === 'requested' ||
          c.status === 'processing' ||
          c.status === 'paid'),
    )
    .reduce((sum, c) => sum + Number(c.amount), 0);
  const ledger = (db.balanceLedger ?? [])
    .filter((e) => e.userId === userId)
    .reduce((sum, e) => sum + Number(e.amount), 0);
  return round2(confirmed - inFlight + ledger);
};

/** The MEMBER-FACING balance: the signed figure floored at 0. */
const deriveBalance = (db: Db, userId: number): number =>
  Math.max(0, deriveBalanceSigned(db, userId));

/**
 * The denormalised `tagKeys` for a merchant_region, DERIVED on read from the
 * merchant_region_tags JOIN (the source of truth — D-multi-channel-editor). The
 * stored tagKeys mirror exists for the real schema, but the mock recomputes it
 * here so it CANNOT drift from the join: attach / detach a join row and the keys
 * follow on the next read, exactly like `clicks` and `storeCount`. Order follows
 * the join rows (the seed inserts them in tagKeys order, so seeded order holds).
 */
const deriveTagKeys = (db: Db, regionId: number): string[] => {
  const keyById = new Map(
    (db.storeTags ?? []).map((t) => [t.id, t.key as string]),
  );
  return (db.merchantRegionTags ?? [])
    .filter((j) => j.merchantRegionId === regionId)
    .map((j) => keyById.get(Number(j.tagId)))
    .filter((k): k is string => typeof k === 'string');
};

/**
 * Return the resource's rows with derived fields injected. Shallow copies so the
 * canonical collection is never mutated; the deep `clone` at the response edge
 * handles nested arrays.
 */
const materialize = (db: Db, resource: string): Row[] => {
  const rows = collection(db, resource);
  switch (resource) {
    case 'events':
      // A flat serialization of the JSONB properties bag (D-events-explorer). The events
      // explorer's free-text `q` scans top-level string values (matchesText), so this
      // mirror lets a search substring-match a property VALUE (e.g. a store_id) without a
      // typed per-key JSONB-path filter. The detail view and the CSV read the structured
      // `properties`; `propertiesText` is search-only and never displayed.
      return rows.map((e) => ({
        ...e,
        propertiesText: JSON.stringify(e.properties ?? {}),
      }));
    case 'users':
      return rows.map((u) => ({
        ...u,
        balance: deriveBalance(db, Number(u.id)),
        balanceSigned: deriveBalanceSigned(db, Number(u.id)),
      }));
    case 'affiliateNetworks':
      return rows.map((n) => ({
        ...n,
        storeCount: (db.merchantRegions ?? []).filter(
          (r) => r.networkId === n.id,
        ).length,
      }));
    case 'merchantRegions':
      return rows.map((r) => ({
        ...r,
        // tagKeys is DERIVED from the join so the mirror cannot drift (the join
        // is the source of truth — D-multi-channel-editor). Tag filters and the
        // store detail both read this, so a tag attach/detach is reflected at once.
        tagKeys: deriveTagKeys(db, Number(r.id)),
        clicks: (db.clicks ?? []).filter((c) => c.merchantRegionId === r.id)
          .length,
      }));
    case 'merchants': {
      return rows.map((m) => {
        const regionIds = new Set(
          (db.merchantRegions ?? [])
            .filter((r) => r.merchantId === m.id)
            .map((r) => r.id),
        );
        const clicks = (db.clicks ?? []).filter((c) =>
          regionIds.has(c.merchantRegionId),
        ).length;
        return { ...m, clicks };
      });
    }
    case 'stores': {
      // Read-only JOIN for the Stores list surface (Phase 1). One row per
      // merchant PER MARKET (the merchant_regions grain), enriched on read with
      // the merchant's brand fields and the resolved category + affiliate
      // network NAMES — the same on-read join used for the derived fields above,
      // so surfaces never touch the seed. The row carries `market` (so the one
      // region->market mapping scopes it) and exposes categoryId / networkId /
      // status as real fields (so the list's filters and sorts work through the
      // standard getList path). Its id is the merchant_region id (stable per
      // market). There is no `stores` collection in the seed; it is derived.
      const merchantsById = new Map((db.merchants ?? []).map((m) => [m.id, m]));
      const categoriesById = new Map(
        (db.categories ?? []).map((c) => [c.id, c]),
      );
      const networksById = new Map(
        (db.affiliateNetworks ?? []).map((n) => [n.id, n]),
      );
      return (db.merchantRegions ?? []).map((r) => {
        const merchant = merchantsById.get(r.merchantId);
        const category =
          merchant != null
            ? categoriesById.get(merchant.categoryId as number)
            : undefined;
        const network =
          r.networkId != null ? networksById.get(r.networkId as number) : null;
        return {
          id: r.id,
          merchantId: r.merchantId,
          merchantRegionId: r.id,
          market: r.market,
          name: (merchant?.name as string) ?? '',
          slug: (merchant?.slug as string) ?? '',
          logoUrl: (merchant?.logoUrl as string | null) ?? null,
          categoryId: (merchant?.categoryId as number | undefined) ?? null,
          category: (category?.name as string) ?? '',
          networkId: (r.networkId as number | null) ?? null,
          network: (network?.name as string | undefined) ?? null,
          baseRate: r.baseRate,
          active: r.active,
          status: r.active ? 'active' : 'inactive',
        } as Row;
      });
    }
    case 'cashbacks': {
      // Read-only JOIN for the Cashbacks list (member-facing transactions).
      // One row per transaction, enriched on read with the resolved MEMBER
      // (name + email via userId) and STORE (merchant name via merchantId) — the
      // same on-read join the other surfaces use, so the list never touches the
      // seed and resolves names, not ids. Carries `market` (the one
      // region->market mapping scopes it) and keeps status / amounts / currency
      // as real fields so filters and sorts work through the standard getList
      // path. Its id is the transaction id.
      //
      // NOTE: `commissionAmount` is deliberately NOT surfaced here. It is the
      // platform margin, reserved for the future Earnings / FinOps surface where
      // margin is the point — do not add it to this member-facing list.
      const usersById = new Map((db.users ?? []).map((u) => [u.id, u]));
      const merchantsById = new Map((db.merchants ?? []).map((m) => [m.id, m]));
      return (db.transactions ?? []).map((t) => {
        const user = usersById.get(t.userId);
        const merchant = merchantsById.get(t.merchantId);
        return {
          id: t.id,
          transactionId: t.id,
          userId: t.userId,
          merchantId: t.merchantId,
          merchantRegionId: t.merchantRegionId,
          market: t.market,
          memberName: (user?.name as string) ?? '',
          memberEmail: (user?.email as string) ?? '',
          storeName: (merchant?.name as string) ?? '',
          grossAmount: t.grossAmount,
          cashbackAmount: t.cashbackAmount,
          currency: t.currency,
          status: t.status,
          // occurredAt (conversion) is the list's date column; createdAt
          // (ingestion) is kept for reference.
          occurredAt: t.occurredAt,
          createdAt: t.createdAt,
        } as Row;
      });
    }
    case 'cashouts': {
      // Read-only JOIN for the Cashouts list (cashout_requests). One row per
      // request, enriched on read with the resolved MEMBER (name + email via
      // userId) and, for donations, the CHARITY name (via charityId). Carries
      // `market` (the one region->market mapping scopes it) and keeps the
      // destination / status / amount / SLA as real fields so filters and sorts
      // work through the standard getList path. Id is the cashout request id.
      //
      // SECURITY: `payoutDetails` (an opaque encrypted envelope) is deliberately
      // NOT surfaced here — never materialised into a list row, decoded, or
      // logged. `slaBreached` is exposed as a boolean for the breach indicator
      // and breached-only filter; the raw `slaBreachedAt` is not needed by the
      // list.
      const usersById = new Map((db.users ?? []).map((u) => [u.id, u]));
      const charitiesById = new Map(
        (db.charities ?? []).map((ch) => [ch.id, ch]),
      );
      return (db.cashoutRequests ?? []).map((c) => {
        const user = usersById.get(c.userId);
        const charity =
          c.charityId != null ? charitiesById.get(c.charityId as number) : null;
        return {
          id: c.id,
          cashoutRequestId: c.id,
          userId: c.userId,
          market: c.market,
          memberName: (user?.name as string) ?? '',
          memberEmail: (user?.email as string) ?? '',
          amount: c.amount,
          currency: c.currency,
          destinationType: c.destinationType,
          payoutMethod: c.payoutMethod ?? null,
          charityId: (c.charityId as number | null) ?? null,
          charityName: (charity?.name as string | undefined) ?? null,
          status: c.status,
          slaBreached: c.slaBreachedAt !== null,
          requestedAt: c.requestedAt,
          paidAt: c.paidAt,
        } as Row;
      });
    }
    case 'audit':
      // The audit trail, read-only. Entries are denormalised (actor, resource
      // and the field diff are stored on the row), so there is nothing to join —
      // the list reads them straight through the standard getList path, and the
      // future real API serves the same shape from `getList('audit')`. Writes
      // arrive via `appendAuditEntries` (the wrapper), never through the audited
      // create/update/delete. (See DECISIONS.md D-audit.)
      return db.auditLog ?? [];
    default:
      return rows;
  }
};

/**
 * Assemble the full STORE DETAIL for one merchant (the brand), keyed by
 * merchantId — the read model behind `getOne('stores', merchantId)` and the
 * Stores detail/edit (D2). Brand fields + every `merchant_regions` row for the
 * merchant, each enriched with its read-only channels, tags and resolved network
 * name. The join lives here (the established pattern), so the surface never
 * touches the seed. ALL markets are returned; the surface filters to the ones the
 * user may see (D2-2). `regionId` on each market is the row a section's edit
 * writes (D2-6). Returns null if the merchant does not exist.
 */
const assembleStore = (db: Db, merchantId: number): Row | null => {
  const merchant = (db.merchants ?? []).find((m) => m.id === merchantId);
  if (!merchant) return null;
  const category = (db.categories ?? []).find(
    (c) => c.id === merchant.categoryId,
  );
  const networksById = new Map(
    (db.affiliateNetworks ?? []).map((n) => [n.id, n]),
  );
  const tagsById = new Map((db.storeTags ?? []).map((t) => [t.id, t]));

  const markets = (db.merchantRegions ?? [])
    .filter((r) => r.merchantId === merchantId)
    .sort((a, b) => String(a.market).localeCompare(String(b.market)))
    .map((r) => {
      const network =
        r.networkId != null ? networksById.get(r.networkId as number) : null;
      const channels = (db.storeChannels ?? [])
        .filter((c) => c.merchantRegionId === r.id)
        .map((c) => ({
          id: c.id,
          merchantRegionId: c.merchantRegionId,
          channelType: c.channelType,
          label: c.label,
          baseRate: c.baseRate,
          trackingUrl: (c.trackingUrl as string | null) ?? null,
          attributionMethodOverride:
            (c.attributionMethodOverride as string | null) ?? null,
          isActive: c.isActive,
        }));
      // Tags come from the JOIN (the source of truth), carrying the join row id
      // so the editor can detach by it; the derived tagKeys mirror stays in lock
      // step (D-multi-channel-editor).
      const tags = (db.merchantRegionTags ?? [])
        .filter((j) => j.merchantRegionId === r.id)
        .map((j) => {
          const tag = tagsById.get(Number(j.tagId));
          return {
            assignmentId: j.id,
            tagId: Number(j.tagId),
            key: (tag?.key as string) ?? '',
            label: (tag?.label as string) ?? (tag?.key as string) ?? '',
          };
        })
        .filter((t) => t.key !== '');
      return {
        regionId: r.id,
        market: r.market,
        baseRate: r.baseRate,
        active: r.active,
        networkId: (r.networkId as number | null) ?? null,
        networkName: (network?.name as string | undefined) ?? null,
        channels,
        tags,
      };
    });

  return {
    id: merchant.id,
    name: merchant.name,
    slug: merchant.slug,
    description: merchant.description,
    logoUrl: (merchant.logoUrl as string | null) ?? null,
    categoryId: (merchant.categoryId as number | undefined) ?? null,
    category: (category?.name as string) ?? '',
    coarseCategory: merchant.coarseCategory,
    founderNames: (merchant.founderNames as string[]) ?? [],
    countryOfHeritage: merchant.countryOfHeritage,
    blackOwnershipEvidenceUrl: merchant.blackOwnershipEvidenceUrl,
    ownershipVerificationStatus: merchant.ownershipVerificationStatus,
    status: merchant.status,
    createdAt: merchant.createdAt,
    markets,
  } as Row;
};

const matchesText = (row: Row, q: string): boolean => {
  const needle = q.toLowerCase();
  return Object.values(row).some(
    (v) => typeof v === 'string' && v.toLowerCase().includes(needle),
  );
};

const applyFilter = (rows: Row[], filter: Record<string, unknown>): Row[] => {
  let result = rows;
  const marketAware = rows.length > 0 && 'market' in rows[0];

  for (const [key, value] of Object.entries(filter ?? {})) {
    if (value === undefined || value === null || value === '') continue;

    if (key === 'q') {
      result = result.filter((row) => matchesText(row, String(value)));
      continue;
    }
    if (key === 'region') {
      if (marketAware) {
        // Access scope -> data markets, applied to market-bearing rows. A 'global' market
        // row (the internal-alerts system_error / suspicious_activity config and its fired
        // instances, D-internal-alerts) is NOT market-specific, so it is visible in EVERY
        // scope. No existing collection uses 'global', so this is additive.
        const markets = marketsForScope(String(value));
        result = result.filter(
          (row) =>
            row.market === 'global' || markets.includes(row.market as Market),
        );
      } else if (rows.length > 0 && 'region' in rows[0]) {
        // Region-bearing but NOT market-bearing rows (audit entries): an
        // EXACT-VALUE match on the stored access scope at the time of the action
        // (D-audit-view). This is the actor's region context, NOT a data market,
        // so it does NOT route through marketsForScope.
        result = result.filter((row) => row.region === value);
      }
      // A region filter on rows with neither market nor region is a no-op.
      continue;
    }
    if (key === 'recordId' || key === 'userId') {
      // recordId (audit) and userId (the events-explorer facet, D-events-explorer) are
      // id fields a text-input facet supplies as a string; compare as strings so a numeric
      // id matches. String-compare is a safe superset: a numeric userId filter (consent /
      // dsar / erasure / impersonation) still matches, since equal numbers stringify equal.
      result = result.filter((row) => String(row[key]) === String(value));
      continue;
    }
    if (key === 'occurredAt_gte' || key === 'occurredAt_lte') {
      // The date-RANGE operator (D-audit-view): the only comparison operator at
      // this seam, scoped to occurredAt for the audit investigative view. The real
      // backend inherits this same filter-param contract. occurredAt_gte = from,
      // occurredAt_lte = to (both inclusive bounds).
      const bound = Date.parse(String(value));
      if (!Number.isNaN(bound)) {
        result = result.filter((row) => {
          const t = Date.parse(String(row.occurredAt));
          return key === 'occurredAt_gte' ? t >= bound : t <= bound;
        });
      }
      continue;
    }
    result = result.filter((row) => {
      const field = row[key];
      // Array field (e.g. merchant_regions.tagKeys): match if it contains the
      // wanted value (or any of the wanted values).
      if (Array.isArray(field)) {
        const wanted = Array.isArray(value) ? value : [value];
        return wanted.some((w) => field.includes(w));
      }
      // Scalar field with a list of allowed values (e.g. ids).
      if (Array.isArray(value)) return value.includes(field);
      // Scalar equality.
      return field === value;
    });
  }
  return result;
};

const applySort = (
  rows: Row[],
  sort?: { field: string; order: string } | null,
): Row[] => {
  if (!sort?.field) return rows;
  const { field, order } = sort;
  const dir = order === 'DESC' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (av === bv) return 0;
    if (av === undefined || av === null) return 1;
    if (bv === undefined || bv === null) return -1;
    return (av < bv ? -1 : 1) * dir;
  });
};

const paginate = (
  rows: Row[],
  pagination?: { page: number; perPage: number } | null,
): Row[] => {
  if (!pagination) return rows;
  const { page, perPage } = pagination;
  const start = (page - 1) * perPage;
  return rows.slice(start, start + perPage);
};

function collection(db: Db, resource: string): Row[] {
  if (!db[resource]) db[resource] = [];
  return db[resource];
}

const nextId = (rows: Row[]): number =>
  rows.reduce((max, row) => Math.max(max, Number(row.id)), 0) + 1;

export function createMockDataProvider(
  dataset: Dataset,
): AdminDataProvider & AuditSink {
  // Clone so session mutations never touch the canonical seed.
  const db: Db = clone(dataset) as unknown as Db;

  // Rows of a market-bearing collection that fall in the region's markets.
  const inRegion = (resource: string, markets: readonly Market[]): Row[] =>
    (db[resource] ?? []).filter((r) => markets.includes(r.market as Market));

  const provider = {
    getList: (resource: string, params: GetListParams) => {
      const all = applyFilter(materialize(db, resource), params.filter ?? {});
      const sorted = applySort(all, params.sort);
      const page = paginate(sorted, params.pagination);
      return Promise.resolve({ data: clone(page), total: all.length });
    },

    getOne: (resource: string, params: GetOneParams) => {
      // `stores` is keyed by MERCHANT id for detail/edit (the brand), assembled
      // across all its markets — distinct from the per-market `stores` LIST rows
      // (the list never calls getOne; the list row links by merchantId). (D2.)
      if (resource === 'stores') {
        const store = assembleStore(db, Number(params.id));
        if (!store) {
          return Promise.reject(
            new Error(`stores #${String(params.id)} not found`),
          );
        }
        return Promise.resolve({ data: clone(store) });
      }
      // Coerce ids to string for comparison: React Admin routing delivers the
      // id from the URL as a string, while the seed ids are numbers. A loose
      // (stringified) match handles both without changing the stored id type.
      const wanted = String(params.id);
      const row = materialize(db, resource).find(
        (r) => String(r.id) === wanted,
      );
      if (!row) {
        return Promise.reject(
          new Error(`${resource} #${String(params.id)} not found`),
        );
      }
      return Promise.resolve({ data: clone(row) });
    },

    getMany: (resource: string, params: GetManyParams) => {
      const ids = new Set(params.ids.map((id) => String(id)));
      const rows = materialize(db, resource).filter((r) =>
        ids.has(String(r.id)),
      );
      return Promise.resolve({ data: clone(rows) });
    },

    getManyReference: (resource: string, params: GetManyReferenceParams) => {
      const referenced = materialize(db, resource).filter(
        (r) => r[params.target] === params.id,
      );
      const filtered = applyFilter(referenced, params.filter ?? {});
      const sorted = applySort(filtered, params.sort);
      const page = paginate(sorted, params.pagination);
      return Promise.resolve({ data: clone(page), total: filtered.length });
    },

    create: (resource: string, params: CreateParams) => {
      const rows = collection(db, resource);
      const row = { ...params.data, id: nextId(rows) } as Row;
      rows.push(row);
      return Promise.resolve({ data: clone(row) });
    },

    update: (resource: string, params: UpdateParams) => {
      const rows = collection(db, resource);
      const index = rows.findIndex((r) => r.id === params.id);
      if (index === -1) {
        return Promise.reject(
          new Error(`${resource} #${String(params.id)} not found`),
        );
      }
      rows[index] = { ...rows[index], ...params.data, id: params.id } as Row;
      return Promise.resolve({ data: clone(rows[index]) });
    },

    updateMany: (resource: string, params: UpdateManyParams) => {
      const rows = collection(db, resource);
      const ids = new Set(params.ids);
      for (let i = 0; i < rows.length; i++) {
        if (ids.has(rows[i].id)) {
          rows[i] = { ...rows[i], ...params.data } as Row;
        }
      }
      return Promise.resolve({ data: [...params.ids] });
    },

    delete: (resource: string, params: DeleteParams) => {
      const rows = collection(db, resource);
      const index = rows.findIndex((r) => r.id === params.id);
      const [removed] =
        index >= 0 ? rows.splice(index, 1) : [params.previousData];
      return Promise.resolve({ data: clone(removed) as Row });
    },

    deleteMany: (resource: string, params: DeleteManyParams) => {
      const ids = new Set(params.ids);
      db[resource] = collection(db, resource).filter((r) => !ids.has(r.id));
      return Promise.resolve({ data: [...params.ids] });
    },

    // The custom read-only AGGREGATE SEAM for the dashboard. Computed here (not
    // in the component) over the canonical collections, scoped to the active
    // region's markets via the SAME one mapping the `region` filter uses. The
    // future real admin API implements this same contract; a future trend chart
    // consumes the same seam. (See DECISIONS.md D-dashboard.)
    getDashboardStats: (params: { region: string }) => {
      const markets = marketsForScope(params.region);
      const sumCashback = (statuses: readonly string[]): number =>
        round2(
          inRegion('transactions', markets)
            .filter((t) => statuses.includes(t.status as string))
            .reduce((sum, t) => sum + Number(t.cashbackAmount), 0),
        );
      const cashouts = inRegion('cashoutRequests', markets);
      const countCashouts = (status: string): number =>
        cashouts.filter((c) => c.status === status).length;

      const market: Market = markets[0] ?? 'uk';
      const currency: Currency = CURRENCY_BY_MARKET[market];
      const cashoutsRequested = countCashouts('requested');
      const cashoutsProcessing = countCashouts('processing');

      return Promise.resolve({
        data: {
          market,
          currency,
          members: inRegion('users', markets).length,
          activeStores: inRegion('merchantRegions', markets).filter(
            (r) => r.active === true,
          ).length,
          pendingCashback: sumCashback(['pending', 'waiting']),
          confirmedCashback: sumCashback(['confirmed']),
          cashoutsAwaitingAction: cashoutsRequested + cashoutsProcessing,
          cashoutsRequested,
          cashoutsProcessing,
          slaBreaches: cashouts.filter((c) => c.slaBreachedAt !== null).length,
        },
      });
    },

    // The AUDIT WRITE SEAM. The provider owns the audit store; the wrapper
    // (./withAudit.ts) calls this to append entries after a mutation. Reads go
    // back out through the normal `getList('audit')` path. Kept off the
    // DataProvider interface surfaces use — it is internal plumbing, not a
    // surface API. (See DECISIONS.md D-audit.)
    appendAuditEntries: (entries: readonly AuditEntryDraft[]) => {
      const rows = collection(db, 'auditLog');
      for (const entry of entries) {
        rows.push({ ...entry, id: nextId(rows) } as Row);
      }
    },
  };

  return provider as unknown as AdminDataProvider & AuditSink;
}
